import useWindowSize from "@DSG/Common/Hooks/useWindowSize";
import colorPallete, { bodyFonts, buttonFonts, elevation, headlineFonts, labelFonts, themeFontWeights } from "@DSG/MobiusOverrides/styles/variables";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Img from "@insite/mobius/Img/Img";
import Tab from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup/TabGroup";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import * as React from "react";
import { FC, useState } from "react";
import { css } from "styled-components";

const enum fields {
    headingContent = "headingContent",
    tabData = "tabData",
    backgroundColor = "backgroundColor",
}

interface TabItem {
    fields: {
        title: string;
        image: string;
        showSameImageForMobile: boolean;
        imageMobile: string;
        content: string;
    }
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.headingContent]: string;
        [fields.tabData]: TabItem[];
        [fields.backgroundColor]: string;
    };
}

export interface DsgVerticalTabNavigationStyles {
    wrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    tabGroup?: TabGroupPresentationProps;
    mobileTabTitle?: InjectableCss;
    headingWrapper?: InjectableCss;
    imageWrapper?: InjectableCss;
    imageDesktop?: HiddenProps;
    imageMobile?: HiddenProps;
    contentWrapper?: InjectableCss;
    contentMainWrapper?: InjectableCss;
}

export const dsgVerticalTabNavigationStyles: DsgVerticalTabNavigationStyles = {
    wrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        margin: 24px 0;
                        padding: 48px 24px;
                        border-radius: 8px;
                    `,
                ],
                "min",
            )}
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        background: transparent !important;
                    `,
                ],
                "max",
            )}
        `
    },
    innerWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    max-width: 978px;
                    margin: 0 auto;
                `,
                ],"min"
            )}
        `
    },
    headingWrapper: {
        css: css`
            > div,
            & {
                display: flex;
                gap: 16px; 
                flex-direction: column;
                > [class*="TypographyStyle"] {
                    margin-bottom: 0;
                }
            }
            p {
                color: ${colorPallete.grey800};
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    margin: 0 auto 48px;
                    max-width: 736px;
                    > div,
                    & {
                        gap: 8px; 
                    }
                `,
                ],"min"
            )}
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        padding: 40px;
                        border-radius: 8px;

                    `,
                ],
                "max",
            )}
        `
    },
    tabGroup: {
        mergeCss: true,
        cssOverrides: {
            wrapper: css`
                padding: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        flex-direction: row;
                        align-items: flex-start;
                        [data-id="tabContent"] {
                            max-width: calc(100% - 325px);
                            padding-left: 24px;
                            width: 100%;
                        }
                    `,
                    css`
                        [data-id="tabContent"] {
                            max-width: calc(100% - 453px);
                            padding-left: 48px;
                        }
                    `,
                    ],"min"
                )}
            `,
            tabGroup: css`
                > li {
                    text-align: left;
                    padding: 12px 24px 12px 16px;
                    font: ${headlineFonts.headline3SemiBoldSm.fontWeight} ${headlineFonts.headline3SemiBoldSm.fontSize}/${headlineFonts.headline3SemiBoldSm.lineHeight} ${headlineFonts.headline3SemiBoldSm.fontFamily};
                    color: ${colorPallete.grey600DisabledText};
                    border: none;
                    cursor: pointer;
                    border-left: 8px solid transparent;
                    margin: 2px 0;
                    &[aria-selected="true"] {
                        background: ${colorPallete.white};
                        position: relative;
                        border-radius: 4px;
                        color: ${colorPallete.grey800};
                        border-left: 8px solid ${colorPallete.primaryRed500};
                    }
                    &:hover {
                        border-bottom: none;
                        outline: none;
                    }
                    &:focus {
                        border-bottom: none;
                    }
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        flex-direction: column;
                        flex-wrap: wrap;
                        flex: initial;
                        max-width: 324px;
                        margin: 0 !important;
                        padding: 0 !important;
                        width: 100%;
                        gap: 6px;
                    `,
                    css`
                        max-width: 453px;
                    `
                    ],"min"
                )}
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                    null,
                    css`
                        display: none;
                        
                    `,
                    ],"max"
                )}
            `,
            tabContent: css`
                margin: 0;
                border: none;
                padding: 0;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        flex-direction: column;

                    `,
                    ],"min"
                )}
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                    null,
                    css`
                        display: block;
                    `,
                    ],"max"
                )}
            `,
        }
    },
    mobileTabTitle: {
        css: css`
            padding: 12px 24px 12px 16px;
            font: ${headlineFonts.headline3SemiBoldSm.fontWeight} ${headlineFonts.headline3SemiBoldSm.fontSize}/${headlineFonts.headline3SemiBoldSm.lineHeight} ${headlineFonts.headline3SemiBoldSm.fontFamily};
            background: ${colorPallete.white};
            border-radius: 4px;
            color: ${colorPallete.grey800};
            border-left: 8px solid ${colorPallete.primaryRed500};
            margin-bottom: 16px;
        `
    },
    imageWrapper: {
        css: css`
            display: flex;
            > div {
                width: 100%;
            }
            img {
                width: 100%;
                height: auto;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                null,
                null,
                css`
                    margin-bottom: 24px; 
                `,
                ],"min"
            )}
        `
    },
    imageDesktop: {
        below: "md",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    null,
                    css`
                        display: flex;
                    `,
                ],
                "min",
            )}
        `
    },
    imageMobile: {
        above: "sm",
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        display: flex;
                    `,
                ],
                "max",
            )}
        `
    },
    contentWrapper: {
        css: css`
            p {
                font: ${bodyFonts.body2RegularLg.fontWeight} ${bodyFonts.body2RegularLg.fontSize}/${bodyFonts.body2RegularLg.lineHeight} ${bodyFonts.body2RegularLg.fontFamily};
                color: ${colorPallete.grey800};
                &:last-child {
                    margin-bottom: 0;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        padding-left: 24px;
                    `,
                ],
                "max",
            )}
        `
    },
    contentMainWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(
                theme,
                [
                    null,
                    css`
                        padding: 40px 16px;
                        border-radius: 8px;
                    `,
                ],
                "max",
            )}
        `
    },
};

const styles = dsgVerticalTabNavigationStyles;

export const DsgVerticalTabNavigation: FC<OwnProps> = ({ fields }) => {
    const { tabData } = fields;
    const tabs = [] as any;
    const [activeTab, setActiveTab] = useState<number | null>(0); 
    const [showContent, setShowContent] = useState<boolean>(true);

    const {width} = useWindowSize();
    let checkMobileScreen = false;
    if(width < 768) {
        checkMobileScreen = true;
    }

    const handleTabChange = (index: number) => {
        setActiveTab(index);
        setShowContent(true);
    };

    {tabData.map((item, index) => (
        tabs.push(
            <Tab headline={item.fields.title} tabKey={`Tab${index}`}  onClick={() => handleTabChange(index)}>
                {showContent && (
                    <StyledWrapper className="tabInnerContentWrapper">
                        {(item.fields.image && item.fields.showSameImageForMobile) && (
                            <StyledWrapper {...styles.imageWrapper}>
                                <Img src={item.fields.image} />  
                            </StyledWrapper>
                        )}
                        {(item.fields.image && !item.fields.showSameImageForMobile) && (
                            <StyledWrapper {...styles.imageWrapper}>
                                {item.fields.image && (
                                    <Hidden {...styles.imageDesktop}>
                                        <Img src={item.fields.image} />
                                    </Hidden>
                                )}
                                {item.fields.imageMobile && (
                                    <Hidden {...styles.imageMobile}>
                                        <Img src={item.fields.imageMobile} />
                                    </Hidden>
                                )}
                            </StyledWrapper>
                        )}
                        <StyledWrapper {...styles.contentMainWrapper}
                            style={{ backgroundColor: checkMobileScreen ? `${fields.backgroundColor}` : '' }}
                        >
                            {item.fields.title && (
                                <Hidden above="sm">
                                    <StyledWrapper {...styles.mobileTabTitle}>
                                        {item.fields.title}
                                    </StyledWrapper>
                                </Hidden>
                            )}
                            {item.fields.content && (
                                <StyledWrapper {...styles.contentWrapper}>
                                    {parse(item.fields.content, parserOptions)}
                                </StyledWrapper>
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </Tab>
        )
    ))}

    return (
        <StyledWrapper {...styles.wrapper}  
            style={{backgroundColor: fields.backgroundColor ? `${fields.backgroundColor}` : '#F6F6F7'}}>
            <StyledWrapper {...styles.innerWrapper}>
                {fields.headingContent && (
                    <StyledWrapper {...styles.headingWrapper} 
                        style={{ backgroundColor: checkMobileScreen ? `${fields.backgroundColor}` : '' }}>
                        {parse(fields.headingContent, parserOptions)}
                    </StyledWrapper>
                )}
                <TabGroup current={`Tab${activeTab}`} {...styles.tabGroup}>
                    {tabs}
                </TabGroup>
            </StyledWrapper>
        </StyledWrapper>
   
    );
};


const widgetModule: WidgetModule = {
    component: DsgVerticalTabNavigation,
    definition: {
        group: "Basic",
        displayName: "Vertical Tab Navigation",
        fieldDefinitions: [
            {
                name: fields.headingContent,
                editorTemplate: "RichTextField",
                displayName: "Top Section Heading Content",
                defaultValue: "",
                fieldType: "General",
                extendedConfig: { height: 170 },
            },
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "#F6F6F7",
            },
            {
                name: fields.tabData,
                editorTemplate: "ListField",
                displayName: "Tab Content",
                getDisplay: (item: HasFields, state) => {
                    const { title } = item.fields;
                    if (title) {
                        return title;
                    }
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "title",
                        editorTemplate: "TextField",
                        displayName: "Title",
                        defaultValue: "",
                        sortOrder: 0,
                        isRequired: true,
                    },
                    {
                        name: "content",
                        editorTemplate: "RichTextField",
                        displayName: "Content",
                        defaultValue: "",
                        extendedConfig: { height: 170 },
                    },
                    {
                        name: "image",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "showSameImageForMobile",
                        displayName: "Show Same Image for Mobile",
                        editorTemplate: "CheckboxField",
                        defaultValue: true,
                    },
                    {
                        name: "imageMobile",
                        displayName: "Image For Mobile",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                        isVisible: widget => !widget.fields.showSameImageForMobile,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;